<template>
  <v-dialog
    v-model="dialog"
    max-width="640"
    open-on-focus
    no-click-animation
    persistent
    content-class="create-journey-dialog__dialog"
  >
    <v-container class="create-journey-dialog__container">
      <v-card elevation="0">
        <v-card-title>
          <h3 class="mb-0 create-journey-dialog__card-title">
            Create New Journey
          </h3>
          <v-spacer />
          <v-btn color="black" icon @click="closeDialog">
            <v-icon size="16">bhi-close</v-icon>
          </v-btn>
        </v-card-title>
      </v-card>
      <div class="create-journey-dialog__content">
        <v-tabs
          class="create-journey-dialog__card-tabs"
          v-model="tab"
          fixed-tabs
        >
          <v-tabs-slider></v-tabs-slider>
          <v-tab :disabled="isGeneratingJourney">Setup</v-tab>
          <v-tab :disabled="!hasJourneyPreview">Preview</v-tab>
        </v-tabs>
        <v-form v-if="tab === 0" ref="journeyForm">
          <journey-form
            :journey-name="journeyName"
            :journey-description="journeyDescription"
            :journey-duration="journeyDuration"
            :journal-frequency="journalFrequency"
            :positive-actions-frequency="positiveActionsFrequency"
            :heroic-choice-frequency="heroicChoiceFrequency"
            :message-time="messageTime"
            :course-steps="courseSteps"
            :force-rules="forceRules"
            :journey-duration-items="journeyDurationItems"
            :journal-frequency-items="journalFrequencyItems"
            :heroic-choice-frequency-items="heroicChoiceFrequencyItems"
            :positive-actions-frequency-items="positiveActionsFrequencyItems"
            :message-time-items="messageTimeItems"
            :course-steps-items="courseStepsItems"
            @update-field="updateField"
            @trigger-validation="formValidation"
          />
        </v-form>
        <div v-else class="create-journey-dialog__card-preview">
          <!-- Loading State -->
          <div v-if="isGeneratingJourney" class="journey-generating-loader">
            <div class="loader-content">
              <v-progress-circular
                indeterminate
                color="accent"
                size="64"
                width="4"
              ></v-progress-circular>
              <h3 class="loader-title">{{ generationJourneyTitle }}</h3>
              <p class="loader-description">
                We're creating your personalized journey with positive actions,
                timed messages, and course content. This usually takes about 30
                seconds.
              </p>
              <div class="loader-steps">
                <div class="step" :class="{ active: true }">
                  <v-icon size="20">mdi-check-circle</v-icon>
                  <span>Journey created</span>
                </div>
                <div class="step" :class="{ active: isGeneratingJourney }">
                  <v-progress-circular
                    v-if="isGeneratingJourney"
                    indeterminate
                    color="accent"
                    size="20"
                    width="2"
                  ></v-progress-circular>
                  <v-icon v-else size="20">mdi-check-circle</v-icon>
                  <span>Generating content</span>
                </div>
                <div class="step">
                  <v-icon size="20">mdi-circle-outline</v-icon>
                  <span>Ready for preview</span>
                </div>
              </div>
            </div>
          </div>

          <!-- Error State -->
          <div v-else-if="generationError" class="journey-generation-error">
            <div class="error-content">
              <v-icon size="64" color="error">mdi-alert-circle-outline</v-icon>
              <h3 class="error-title">Generation Failed</h3>
              <p class="error-description">
                We couldn't generate your journey within the expected time. This
                might be due to high server load or temporary issues.
              </p>
              <p class="error-suggestion">
                Would you like to try generating your journey again?
              </p>
            </div>
          </div>

          <!-- Preview Content -->
          <template v-else>
            <div class="display">
              <v-tabs
                class="tabs"
                v-model="additionalTab"
                color="accent"
                height="24"
              >
                <v-tabs-slider></v-tabs-slider>
                <v-tab>Positive Actions - {{ positiveActionsCount }}</v-tab>
                <v-tab>Timed Messages - {{ timedMessagesCount }}</v-tab>
                <v-tab>Course - {{ courseLessonsCount }} Steps</v-tab>
              </v-tabs>
            </div>

            <div class="regenerate-btn-container">
              <v-btn
                class="regenerate-btn"
                rounded
                color="transparent"
                small
                elevation="0"
                :loading="isGeneratingJourney"
                :disabled="
                  isGeneratingJourney ||
                    !currentJourneyId ||
                    isItemRegenerating ||
                    isAnyItemEditing
                "
                @click="handleRegenerateAll"
              >
                <span v-if="!isGeneratingJourney">Regenerate all</span>
                <span v-else>Regenerating...</span>
                <v-icon size="16">bhi-cached</v-icon>
              </v-btn>
            </div>

            <!-- Journey Day Cards Preview -->
            <div
              class="journey-preview-cards"
              :class="{
                'journey-preview-cards--row':
                  additionalTab === 0 || additionalTab === 1,
                'journey-preview-cards--column': additionalTab === 2,
              }"
            >
              <!-- Positive Actions Tab (Row Layout) -->
              <template v-if="additionalTab === 0">
                <journey-day-card
                  v-for="(action, index) in positiveActions"
                  :key="`action-${index}`"
                  :day="action.day"
                  :title="action.title"
                  :description="action.description"
                  :chip-label="action.chipLabel"
                  :chip-color="action.chipColor"
                  :tabs="['AM', 'PM']"
                  :active-tab="action.activeTab"
                  :show-actions="true"
                  :is-regenerating="isRegeneratingItem(action.id)"
                  :is-saving="isSaving && editingItem === action.id"
                  :disabled="
                    isItemRegenerating ||
                      (editingItem && editingItem !== action.id)
                  "
                  :is-editing="isEditing(action.id)"
                  :editing-data="editingItem === action.id ? editingData : null"
                  @tab-change="onTabChange"
                  @edit="onEdit(action.id)"
                  @regenerate="onRegenerate(action.id)"
                  @update-editing-data="updateEditingData"
                  @cancel-edit="onCancelEdit"
                />
              </template>

              <!-- Timed Messages Tab (Row Layout) -->
              <template v-if="additionalTab === 1">
                <journey-day-card
                  v-for="(message, index) in timedMessages"
                  :key="`message-${index}`"
                  :day="message.day"
                  :title="''"
                  :description="message.description"
                  :chip-label="message.chipLabel"
                  :chip-color="message.chipColor"
                  :tabs="['AM', 'PM']"
                  :active-tab="message.activeTab"
                  :show-actions="true"
                  :is-regenerating="isRegeneratingItem(message.id)"
                  :is-saving="isSaving && editingItem === message.id"
                  :disabled="
                    isItemRegenerating ||
                      (editingItem && editingItem !== message.id)
                  "
                  :is-editing="isEditing(message.id)"
                  :editing-data="
                    editingItem === message.id ? editingData : null
                  "
                  @tab-change="onTabChange"
                  @edit="onEdit(message.id)"
                  @regenerate="onRegenerate(message.id)"
                  @update-editing-data="updateEditingData"
                  @cancel-edit="onCancelEdit"
                />
              </template>

              <!-- Course Tab (Column Layout with Scroll) -->
              <template v-if="additionalTab === 2">
                <journey-day-card
                  v-for="(lesson, index) in courseLessons"
                  :key="`lesson-${index}`"
                  :day="lesson.day"
                  :title="lesson.title"
                  :description="lesson.description"
                  :chip-label="lesson.chipLabel"
                  :chip-color="lesson.chipColor"
                  :tabs="[]"
                  :active-tab="''"
                  :show-actions="true"
                  :is-regenerating="isRegeneratingItem(lesson.id)"
                  :is-saving="isSaving && editingItem === lesson.id"
                  :disabled="
                    isItemRegenerating ||
                      (editingItem && editingItem !== lesson.id)
                  "
                  :is-editing="isEditing(lesson.id)"
                  :editing-data="editingItem === lesson.id ? editingData : null"
                  @tab-change="onTabChange"
                  @edit="onEdit(lesson.id)"
                  @regenerate="onRegenerate(lesson.id)"
                  @update-editing-data="updateEditingData"
                  @cancel-edit="onCancelEdit"
                />
              </template>
            </div>
          </template>
        </div>
      </div>
      <v-card-actions class="create-journey-dialog__card-actions">
        <v-btn
          rounded
          text
          elevation="0"
          color="accent"
          large
          @click="closeDialog"
          >Cancel</v-btn
        >
        <v-btn
          v-if="tab === 0"
          rounded
          color="accent"
          class="create-journey-dialog__card-actions-btn"
          large
          :loading="loading"
          :disabled="loading"
          @click="handleGenerateJourney"
        >
          <span v-if="!loading">Generate Journey</span>
          <span v-else>Generating...</span>
        </v-btn>
        <v-btn
          v-else-if="!generationError"
          rounded
          color="accent"
          class="create-journey-dialog__card-actions-btn"
          large
          :disabled="
            !hasJourneyPreview ||
              isGeneratingJourney ||
              generationError ||
              isItemRegenerating ||
              isAnyItemEditing ||
              isSaving
          "
          :loading="loading || isGeneratingJourney"
          @click="approveJourney"
          >Approve</v-btn
        >
        <v-btn
          v-else-if="generationError"
          rounded
          color="accent"
          class="retry-btn"
          large
          :loading="loading"
          :disabled="loading"
          @click="retryGeneration"
        >
          <v-icon left size="20">mdi-refresh</v-icon>
          Try Again
        </v-btn>
      </v-card-actions>
    </v-container>
  </v-dialog>
</template>

<script>
import { mapState, mapActions, mapMutations } from 'vuex'
import moment from 'moment'
import JourneyForm from '@/components/journeys/JourneyForm.vue'
import JourneyDayCard from '@/components/journeys/JourneyDayCard.vue'
import { debounce } from '@/helpers/debounce'
import {
  getChipColor,
  getTimeChipColor,
  getTimeActiveTab,
} from '@/helpers/journeyChipHelpers'

export default {
  components: {
    JourneyForm,
    JourneyDayCard,
  },
  name: 'CreateJourneyDialog',
  props: {
    showDialog: {
      type: Boolean,
      default: false,
    },
    profileId: {
      type: String,
      required: true,
    },
  },
  data: () => ({
    dialog: false,
    journeyName: '',
    journeyDescription: '',
    tab: null,
    additionalTab: 0,
    forceRules: false,
    journeyDuration: 'none',
    journalFrequency: 'none',
    heroicChoiceFrequency: 'none',
    positiveActionsFrequency: 'none',
    messageTime: 'none',
    courseSteps: 'none',
    isGeneratingJourney: false,
    pollingInterval: null,
    currentJourneyId: null,
    generationTimeout: false,
    generationError: false,
    retryTimeout: null,
    isItemRegenerating: false,
    regeneratingItems: new Set(), // Track which items are being regenerated
    editingItem: null, // Track which item is being edited
    editingData: {}, // Store temporary edit data
    isSaving: false, // Track if currently saving edits
    generationJourneyTitle: 'Generating Journey',
  }),
  watch: {
    showDialog: function(val) {
      if (val) {
        this.dialog = true
      }
    },
    additionalTab: function(val) {
      // Clear editing state
      this.onCancelEdit()
    },
  },
  beforeDestroy() {
    // Cleanup polling when component is destroyed
    this.stopPolling()
  },
  computed: {
    ...mapState('journeysStates', ['loading', 'journey']),
    hasJourneyPreview() {
      return this.journey && this.journey.id
    },
    journeyDurationItems() {
      return [
        { text: 'None', value: 'none' },
        { text: '1 week (7 days)', value: '7 days' },
        { text: '2 weeks (14 days)', value: '14 days' },
        { text: '3 weeks (21 days)', value: '21 days' },
        { text: '4 weeks (28 days)', value: '28 days' },
      ]
    },
    journalFrequencyItems() {
      return [
        { text: 'None', value: 'none' },
        { text: 'Daily', value: 'daily' },
        { text: 'Every other day', value: 'every other day' },
        { text: 'Every 3rd day', value: 'every 3rd day' },
        { text: 'Weekly', value: 'weekly' },
      ]
    },
    heroicChoiceFrequencyItems() {
      return [
        { text: 'None', value: 'none' },
        { text: 'Daily', value: 'daily' },
        { text: 'Every other day', value: 'every other day' },
        { text: 'Every 3rd day', value: 'every 3rd day' },
        { text: 'Weekly', value: 'weekly' },
      ]
    },
    positiveActionsFrequencyItems() {
      return [
        { text: 'None', value: 'none' },
        { text: 'Daily', value: 'daily' },
        { text: 'Every other day', value: 'every other day' },
        { text: 'Every 3rd day', value: 'every 3rd day' },
        { text: 'Weekly', value: 'weekly' },
      ]
    },
    messageTimeItems() {
      return [
        { text: 'None', value: 'none' },
        {
          text: '7:00 AM - Morning Motivation',
          value: '7:00 AM - Morning Motivation',
        },
        {
          text: '12:00 PM - Midday check-in',
          value: '12:00',
        },
        {
          text: '2:00 PM - Afternoon support',
          value: '14:00',
        },
        {
          text: '5:00 PM - End of work day',
          value: '17:00',
        },
        {
          text: '7:00 PM - Evening support',
          value: '19:00',
        },
        {
          text: '9:00 PM - Bedtime preparation',
          value: '21:00',
        },
      ]
    },
    courseStepsItems() {
      return [
        { text: 'None', value: 'none' },
        { text: '1 step', value: '1 step' },
        { text: '2 steps', value: '2 steps' },
        { text: '3 steps', value: '3 steps' },
        { text: '4 steps', value: '4 steps' },
        { text: '5 steps', value: '5 steps' },
        { text: '6 steps', value: '6 steps' },
        { text: '8 steps', value: '8 steps' },
        { text: '10 steps', value: '10 steps' },
        { text: '12 steps', value: '12 steps' },
      ]
    },
    positiveActionsCount() {
      return this.journey?.content?.positiveActions?.length || 0
    },
    timedMessagesCount() {
      return this.journey?.content?.timedMessages?.length || 0
    },
    courseLessonsCount() {
      return this.journey?.content?.course?.lessons?.length || 0
    },
    positiveActions() {
      if (this.journey?.content?.positiveActions) {
        return this.journey.content.positiveActions.map(action => ({
          day: `Day ${action.day}`,
          title: action.title,
          description: action.description,
          chipLabel: action.label,
          chipColor: getChipColor(action.time),
          activeTab: action.time === 'MORNING' ? 'AM' : 'PM',
          id: action.id,
        }))
      }
      return []
    },
    timedMessages() {
      if (this.journey?.content?.timedMessages) {
        return this.journey.content.timedMessages.map(message => ({
          day: `Day ${message.day}`,
          title: this.getTimeTitle(message.time),
          description: message.message,
          chipLabel: message.label,
          chipColor: getTimeChipColor(message.time),
          activeTab: getTimeActiveTab(message.time),
          id: message.id,
        }))
      }
      return []
    },
    courseLessons() {
      if (this.journey?.content?.course?.lessons) {
        return this.journey.content.course.lessons.map((lesson, index) => ({
          day: `Lesson ${index + 1}`,
          title: lesson.title,
          description: lesson.description,
          chipLabel: lesson.label,
          chipColor: 'green',
          activeTab: 'AM',
          id: lesson.id,
        }))
      }
      return []
    },
    isRegeneratingItem() {
      return itemId => this.regeneratingItems.has(itemId)
    },
    isAnyItemEditing() {
      return this.editingItem !== null
    },
  },
  methods: {
    ...mapMutations('appStates', ['setSnackbarNotification']),
    ...mapActions('journeysStates', {
      generateJourneyAction: 'generateJourney',
      getJourneyAction: 'getJourney',
      acceptJourneyAction: 'acceptJourney',
      getJourneysAction: 'getJourneys',
      regenerateAllJourneyAction: 'regenerateAllJourney',
      regeneratePositiveActionsJourneyAction:
        'regeneratePositiveActionsJourney',
      regenerateTimedMessagesJourneyAction: 'regenerateTimedMessagesJourney',
      regenerateCoursesLessonJourneyAction: 'regenerateCoursesLessonJourney',
      updatePositiveActionsJourneyAction: 'updatePositiveActionsJourney',
      updateTimedMessagesJourneyAction: 'updateTimedMessagesJourney',
      updateCoursesLessonJourneyAction: 'updateCoursesLessonJourney',
      regenerateAllPositiveActionsJourneyAction:
        'regenerateAllPositiveActionsJourney',
      regenerateAllTimedMessagesJourneyAction:
        'regenerateAllTimedMessagesJourney',
      regenerateAllCoursesJourneyAction: 'regenerateAllCoursesJourney',
    }),
    ...mapMutations('journeysStates', [
      'resetJourney',
      'setJourney',
      'updatePositiveAction',
      'updateTimedMessage',
      'updateCourseLesson',
    ]),
    updateField(field, value) {
      this[field] = value
    },
    handleGenerateJourney() {
      this.generateJourney()
    },
    handleRegenerateAll() {
      this.regenerateAll()
    },
    resetFormAndState() {
      // Reset all form fields to initial state
      this.journeyName = ''
      this.journeyDescription = ''
      this.journeyDuration = 'none'
      this.journalFrequency = 'none'
      this.heroicChoiceFrequency = 'none'
      this.positiveActionsFrequency = 'none'
      this.messageTime = 'none'
      this.courseSteps = 'none'

      // Reset component state variables
      this.tab = null
      this.additionalTab = 0
      this.isGeneratingJourney = false
      this.currentJourneyId = null
      this.generationTimeout = false
      this.generationError = false
      this.isItemRegenerating = false
      this.regeneratingItems.clear()
      this.editingItem = null
      this.editingData = {}
      this.isSaving = false
      this.generationJourneyTitle = 'Generating Journey'

      // Stop any active polling
      this.stopPolling()

      // Reset journey state in store
      this.resetJourney()
    },
    setGenerationJourneyTitle(tab) {
      switch (tab) {
        case 0:
          this.generationJourneyTitle =
            'Generating new Positive Actions Journey'
          break
        case 1:
          this.generationJourneyTitle = 'Generating new Timed Messages Journey'
          break
        case 2:
          this.generationJourneyTitle = 'Generating new Course Lessons Journey'
          break
        default:
          this.generationJourneyTitle = 'Generating Journey'
          break
      }
    },
    async approveJourney() {
      if (!this.hasJourneyPreview) {
        return
      }

      try {
        const startDate = moment().format('YYYY-MM-DD')

        const response = await this.acceptJourneyAction({
          profileId: this.profileId,
          journeyId: this.journey.id,
          startDate: startDate,
        })

        if (response && !response.error) {
          // Reset form fields and preview state to initial state
          this.resetFormAndState()

          // Close the modal
          this.closeDialog()

          // Refresh the journeys list
          await this.getJourneysAction({
            profileId: this.profileId,
          })
        }
      } catch (error) {
        console.error('Error approving journey:', error)
      }
    },
    async formValidation(isRetry = false) {
      let isValid = false

      if (isRetry) {
        if (
          this.journeyDuration === 'none' &&
          this.positiveActionsFrequency === 'none' &&
          this.journalFrequency === 'none' &&
          this.courseSteps === 'none' &&
          this.messageTime === 'none'
        ) {
          // All options disabled
          return false
        }

        if (
          this.messageTime === 'none' &&
          (this.journalFrequency !== 'none' ||
            (this.journeyDuration !== 'none' &&
              this.positiveActionsFrequency === 'none'))
        ) {
          // No message time, but have values for related fields
          return false
        }

        if (
          this.journalFrequency === 'none' &&
          (this.messageTime !== 'none' ||
            (this.journeyDuration !== 'none' &&
              this.positiveActionsFrequency === 'none'))
        ) {
          // No journalFrequency, but have values for related fields
          return false
        }

        if (
          this.positiveActionsFrequency === 'none' &&
          this.journeyDuration !== 'none' &&
          this.journalFrequency === 'none' &&
          this.messageTime === 'none'
        ) {
          // No positive actions frequency, but have duration
          return false
        }

        if (
          this.journeyDuration === 'none' &&
          (this.positiveActionsFrequency !== 'none' ||
            this.messageTime !== 'none' ||
            this.journalFrequency !== 'none')
        ) {
          // No journeyDuration, but have values for related fields
          return false
        }

        return true
      }

      isValid = await this.$refs?.journeyForm?.validate?.()

      return isValid === true
    },
    async generateJourney(isRetry = false) {
      // Validate required fields
      const isValid = await this.formValidation(isRetry)

      const title = `${this.journeyName || ''}`.trim()
      const description = `${this.journeyDescription || ''}`.trim()

      const textFieldsValid = title.length > 0 && description.length > 0

      if (!isValid || !textFieldsValid) {
        this.forceRules = true
        this.setSnackbarNotification(
          `Form contains errors. Please see fields marked in red.`,
        )

        this.generationError = true

        // Go to form tab
        if (this.tab !== 0) {
          this.tab = 0

          // after changing tab, show validation on form
          this.$nextTick(() => {
            this.$refs?.journeyForm?.validate?.()
          })
        }

        return
      }

      try {
        const response = await this.generateJourneyAction({
          profileId: this.profileId,
          title,
          duration: this.journeyDuration.trim(),
          journalFrequency: this.journalFrequency.trim(),
          heroicChoiceFrequency: this.heroicChoiceFrequency.trim(),
          positiveActionsFrequency: this.positiveActionsFrequency.trim(),
          messageTime: this.messageTime.trim(),
          courseSteps: this.courseSteps.trim(),
          description,
        })

        if (response && response.data && response.data.id) {
          // Store the journey ID and start polling
          this.currentJourneyId = response.data.id
          this.isGeneratingJourney = true

          // Switch to preview tab immediately to show loader
          this.tab = 1

          // Start polling for journey completion
          this.startPollingForJourney()
        } else {
          console.error('Invalid response from generateJourney:', response)
        }
      } catch (error) {
        console.error('Error generating journey:', error)
        this.isGeneratingJourney = false
      }
    },
    async regenerateAll() {
      // Validate that we have a current journey ID
      if (!this.currentJourneyId) {
        console.error('No current journey ID available for regeneration')
        return
      }

      try {
        // Set loading state
        this.isGeneratingJourney = true
        this.generationError = false
        this.generationTimeout = false

        const positiveActions = this.additionalTab === 0
        const timedMessages = this.additionalTab === 1
        const courseLessons = this.additionalTab === 2

        let response

        if (positiveActions) {
          response = await this.regenerateAllPositiveActionsJourneyAction({
            profileId: this.profileId,
            journeyId: this.currentJourneyId,
          })
        }

        if (timedMessages) {
          response = await this.regenerateAllTimedMessagesJourneyAction({
            profileId: this.profileId,
            journeyId: this.currentJourneyId,
          })
        }

        if (courseLessons) {
          response = await this.regenerateAllCoursesJourneyAction({
            profileId: this.profileId,
            journeyId: this.currentJourneyId,
          })
        }

        if (response && response.data && response.data.id) {
          // Update current journey ID if it changed
          this.currentJourneyId = response.data.id
          this.setJourney(response.data)

          // Set generation journey title
          this.setGenerationJourneyTitle(this.additionalTab)

          // Start polling for journey completion
          this.startPollingForJourney()
        } else {
          console.error('Invalid response from regenerateAllJourney:', response)
          this.isGeneratingJourney = false
        }
      } catch (error) {
        console.error('Error regenerating journey:', error)
        this.isGeneratingJourney = false
        this.generationError = true
      }
    },

    async startPollingForJourney() {
      if (!this.currentJourneyId) return

      // Clear any existing polling interval
      if (this.pollingInterval) {
        clearInterval(this.pollingInterval)
      }

      // Start polling every 5 seconds
      this.pollingInterval = setInterval(async () => {
        try {
          const response = await this.getJourneyAction({
            profileId: this.profileId,
            journeyId: this.currentJourneyId,
            silent: true,
          })

          // Check if response contains error based on code
          if (
            response.data.status === 'ERROR' ||
            (response.code && (response.code >= 400 || response.code < 200))
          ) {
            console.error('Error in journey response:', response)
            this.stopPolling()
            this.isGeneratingJourney = false
            this.generationError = true
            return
          }

          // Check if journey has all required content
          if (this.isJourneyComplete(response.data)) {
            this.stopPolling()
            this.isGeneratingJourney = false
          }
        } catch (error) {
          console.error('Error polling journey:', error)
          this.stopPolling()
          this.isGeneratingJourney = false
          this.generationError = true
        }
      }, 5000) // Poll every 5 seconds

      // Set a maximum timeout of 5 minutes (60 polls)
      this.retryTimeout = setTimeout(async () => {
        if (this.isGeneratingJourney) {
          console.warn('Journey generation timeout reached')

          // Check the final status before showing error
          try {
            const response = await this.getJourneyAction({
              profileId: this.profileId,
              journeyId: this.currentJourneyId,
              silent: true,
            })

            if (response.data && response.data.status === 'IN_PROGRESS') {
              this.generationTimeout = true
              this.generationError = true
              this.isGeneratingJourney = false
              this.stopPolling()
            } else {
              // Journey completed during timeout check
              this.stopPolling()
              this.isGeneratingJourney = false
            }
          } catch (error) {
            console.error('Error checking final journey status:', error)
            this.generationTimeout = true
            this.generationError = true
            this.isGeneratingJourney = false
            this.stopPolling()
          }
        }
      }, 10 * 60 * 1000) // 10 minutes timeout (120 polls)
    },

    stopPolling() {
      if (this.pollingInterval) {
        clearInterval(this.pollingInterval)
        this.pollingInterval = null
      }
      if (this.retryTimeout) {
        clearTimeout(this.retryTimeout)
        this.retryTimeout = null
      }
    },

    isJourneyComplete(journey) {
      if (!journey) return false

      const { status } = journey

      return status === 'DONE'
    },

    async retryGeneration() {
      // Reset error states
      this.generationError = false
      this.generationTimeout = false
      this.currentJourneyId = null

      // Restart the generation process
      await this.generateJourney(true)
    },
    closeDialog() {
      // Reset form and state
      this.resetFormAndState()

      // Close dialog
      this.dialog = false
      this.$emit('close-dialog')
    },
    onTabChange(newTab) {
      // Convert AM/PM to time format and update editing data
      const timeValue = newTab === 'AM' ? 'MORNING' : 'EVENING'
      this.updateEditingData('time', timeValue)
    },
    onEdit(itemId) {
      if (this.editingItem === itemId) {
        // If already editing this item, save changes
        this.saveEdit()
      } else {
        // Start editing this item
        this.startEdit(itemId)
      }
    },
    onCancelEdit() {
      // Check if we're currently editing an item
      if (this.editingItem) {
        // Store the item ID before resetting it
        const itemId = this.editingItem

        // Reset editing state
        this.editingItem = null
        this.editingData = {}
        this.isSaving = false

        // Ensure any pending operations are cleared
        if (this.regeneratingItems.has(itemId)) {
          this.regeneratingItems.delete(itemId)
        }

        // Reset item regeneration state if no items are being regenerated
        if (this.regeneratingItems.size === 0) {
          this.isItemRegenerating = false
        }
      }
    },
    startEdit(itemId) {
      // Find the item to edit
      const positiveAction = this.journey?.content?.positiveActions?.find(
        action => action.id === itemId,
      )
      const timedMessage = this.journey?.content?.timedMessages?.find(
        message => message.id === itemId,
      )
      const courseLesson = this.journey?.content?.course?.lessons?.find(
        lesson => lesson.id === itemId,
      )

      if (positiveAction) {
        this.editingItem = itemId
        this.editingData = {
          type: 'positiveAction',
          title: positiveAction.title,
          description: positiveAction.description,
          time: positiveAction.time,
        }
      } else if (timedMessage) {
        this.editingItem = itemId
        // Handle time format - convert from HH:mm:ss string to object for editing
        let timeObject = timedMessage.time
        if (typeof timedMessage.time === 'string') {
          // Parse HH:mm:ss format to object
          const [hour, minute] = timedMessage.time.split(':').map(Number)
          timeObject = { hour, minute, second: 0, nano: 0 }
        } else if (timedMessage.time && typeof timedMessage.time === 'object') {
          // Ensure the object has hour and minute properties
          timeObject = {
            hour: timedMessage.time.hour || 0,
            minute: timedMessage.time.minute || 0,
            second: timedMessage.time.second || 0,
            nano: timedMessage.time.nano || 0,
          }
        } else {
          // Fallback if time is null/undefined
          timeObject = { hour: 0, minute: 0, second: 0, nano: 0 }
        }
        // Determine timeOfDay based on the hour
        const hour = timeObject.hour || 0
        const timeOfDay = hour < 12 ? 'AM' : 'PM'

        this.editingData = {
          type: 'timedMessage',
          day: timedMessage.day,
          description: timedMessage.message,
          time: timeObject,
          timeOfDay: timeOfDay,
        }
      } else if (courseLesson) {
        this.editingItem = itemId
        this.editingData = {
          type: 'courseLesson',
          title: courseLesson.title,
          description: courseLesson.description,
          order: courseLesson.order,
        }
      }
    },
    async saveEdit() {
      if (!this.editingItem || !this.currentJourneyId) {
        return
      }

      try {
        this.isSaving = true
        let response

        if (this.editingData.type === 'positiveAction') {
          response = await this.updatePositiveActionsJourneyAction({
            profileId: this.profileId,
            journeyId: this.currentJourneyId,
            positiveActionId: this.editingItem,
            title: this.editingData.title,
            description: this.editingData.description,
            time: this.editingData.time,
          })
        } else if (this.editingData.type === 'timedMessage') {
          // Convert time object to HH:mm:ss format
          let timeString
          if (typeof this.editingData.time === 'string') {
            timeString = this.editingData.time
          } else if (
            this.editingData.time &&
            typeof this.editingData.time === 'object'
          ) {
            const hour = String(this.editingData.time.hour || 0).padStart(
              2,
              '0',
            )
            const minute = String(this.editingData.time.minute || 0).padStart(
              2,
              '0',
            )
            timeString = `${hour}:${minute}:00`
          } else {
            timeString = '00:00:00'
          }

          response = await this.updateTimedMessagesJourneyAction({
            profileId: this.profileId,
            journeyId: this.currentJourneyId,
            timedMessageId: this.editingItem,
            day: this.editingData.day,
            message: this.editingData.description,
            time: timeString,
          })
        } else if (this.editingData.type === 'courseLesson') {
          const courseId = this.journey?.content?.course?.id
          if (courseId) {
            response = await this.updateCoursesLessonJourneyAction({
              profileId: this.profileId,
              journeyId: this.currentJourneyId,
              courseId: courseId,
              lessonId: this.editingItem,
              title: this.editingData.title,
              description: this.editingData.description,
              order: this.editingData.order,
            })
          }
        }

        if (response) {
          // refresh the journey and add loading state
          debounce(
            () => {
              this.getJourneyAction({
                profileId: this.profileId,
                journeyId: this.currentJourneyId,
              }).finally(() => {
                this.isSaving = false
              })
            },
            1000,
            'refreshJourney',
          )
        } else {
          this.isSaving = false
        }

        // Clear edit state
        this.editingItem = null
        this.editingData = {}
      } catch (error) {
        console.error('Error saving edit:', error)
        this.isSaving = false
      }
    },
    updateEditingData(field, value) {
      if (this.editingItem) {
        this.editingData[field] = value
      }
    },
    isEditing(itemId) {
      return this.editingItem === itemId
    },
    async onRegenerate(itemId) {
      if (!this.currentJourneyId || this.regeneratingItems.has(itemId)) {
        return
      }

      try {
        // Add item to regenerating set
        this.regeneratingItems.add(itemId)
        this.isItemRegenerating = true

        // Determine the type of item and call appropriate API
        const positiveAction = this.journey?.content?.positiveActions?.find(
          action => action.id === itemId,
        )
        const timedMessage = this.journey?.content?.timedMessages?.find(
          message => message.id === itemId,
        )
        const courseLesson = this.journey?.content?.course?.lessons?.find(
          lesson => lesson.id === itemId,
        )

        let response

        if (positiveAction) {
          response = await this.regeneratePositiveActionsJourneyAction({
            profileId: this.profileId,
            journeyId: this.currentJourneyId,
            positiveActionId: itemId,
          })
        } else if (timedMessage) {
          response = await this.regenerateTimedMessagesJourneyAction({
            profileId: this.profileId,
            journeyId: this.currentJourneyId,
            timedMessageId: itemId,
          })
        } else if (courseLesson) {
          // For course lessons, we need to find the course ID
          const courseId = this.journey?.content?.course?.id
          if (courseId) {
            response = await this.regenerateCoursesLessonJourneyAction({
              profileId: this.profileId,
              journeyId: this.currentJourneyId,
              courseId: courseId,
              lessonId: itemId,
            })
          }
        }

        if (response && response.data) {
          // Update the specific item in the journey state
          if (positiveAction) {
            this.updatePositiveAction({
              positiveActionId: itemId,
              updatedAction: response.data,
            })
          } else if (timedMessage) {
            this.updateTimedMessage({
              timedMessageId: itemId,
              updatedMessage: response.data,
            })
          } else if (courseLesson) {
            this.updateCourseLesson({
              lessonId: itemId,
              updatedLesson: response.data,
            })
          }
        }
      } catch (error) {
        console.error('Error regenerating item:', error)
        this.isItemRegenerating = false
      } finally {
        // Remove item from regenerating set
        this.regeneratingItems.delete(itemId)
        this.isItemRegenerating = false
      }
    },
    getTimeTitle(time) {
      let hour
      if (typeof time === 'string') {
        // Parse HH:mm:ss format
        hour = parseInt(time.split(':')[0], 10)
      } else {
        hour = time.hour
      }
      if (hour < 12) {
        return 'Morning Motivation'
      } else if (hour < 17) {
        return 'Lunch Break Reminder'
      } else {
        return 'Evening Check-in'
      }
    },
    triggerValidation() {
      this.$refs?.journeyForm?.validate?.()
    },
  },
  mounted() {
    this.triggerValidation()
  },
}
</script>

<style lang="scss">
@import '@/styles/variables.scss';

.create-journey-dialog {
  &__dialog {
    border-radius: 16px;
    overflow: hidden;
    background-color: $bh-white;

    .v-card {
      &__title {
        padding: 0 12px 24px;
      }
    }
  }
  &__container {
    padding: 24px 12px;
    max-height: 90vh;
    display: flex;
    flex-direction: column;
  }
  &__content {
    padding: 0 12px;
    flex: 1;
    overflow: scroll;

    &::-webkit-scrollbar {
      -webkit-appearance: none;
      width: 7px;
    }

    &::-webkit-scrollbar-thumb {
      border-radius: 4px;
      background-color: rgba(0, 0, 0, 0.5);
      box-shadow: 0 0 1px rgba(255, 255, 255, 0.5);
    }
  }
  &__card {
    border-radius: 16px;
    overflow: hidden;
  }
  &__card-text {
    padding: 0;
  }
  &__card-actions {
    padding: 0;
    display: flex;
    flex-direction: row;
    justify-content: flex-end;
    gap: 8px;
    margin-top: 16px;
    &-btn {
      padding: 16px 24px !important;
    }
    .retry-btn {
      padding: 0 16px !important;
    }
  }
  &__card-title {
    color: $bh-black;
    font-size: 1.5715rem;
    font-weight: 600;
  }
  &__card-tabs {
    border-bottom: 1px solid $bh-gray--4;

    .v-tabs-slider-wrapper {
      height: 1px !important;
    }
    .v-tabs-slider {
      background-color: $bh-blue;
    }
    .v-tab {
      color: $bh-blue;
      text-transform: none;
      letter-spacing: 0;
      font-size: 12px;
      font-weight: 600;
      &:not(.v-tab--active) {
        color: $bh-black;
      }

      &:before {
        background-color: $bh-blue;
      }
    }
    &--preview {
      border-bottom: none;
      border-radius: 24px;
      &.v-tabs {
        > .v-tabs-bar {
          background-color: $bh-blue--4;

          .v-tab {
            border-radius: 24px;
            &--active {
              &::before {
                background-color: $bh-blue;
                opacity: 1;
                border-radius: 24px;
                z-index: -1;
                height: 24px;
                transform: translateY(-50%);
                top: 50%;
                width: calc(100% - 8px);
                left: 8px;
              }
            }
          }
        }
      }
      .v-tab {
        font-weight: 700;
        color: $bh-white;
        &:not(.v-tab--active) {
          color: $bh-black !important;
        }
      }
    }
  }
}
.display {
  padding: 8px;
  background-color: $bh-blue--4;
  border-radius: 9999px;
  margin-top: 24px;
}
.tabs {
  &.v-tabs {
    // width: 600px;
    margin: 0 auto;
    background: $bh-white;
    border-radius: 9999px;
    .v-tabs-slider-wrapper {
      height: 100% !important;
      border-radius: 999px !important;
      overflow: hidden;
      z-index: -1;
    }
    .v-slide-group__wrapper {
      border-radius: 999px;
    }
    & > .v-tabs-bar {
      .v-tab {
        transition-duration: 500ms !important;
        transition-property: color;
        text-transform: none;
        letter-spacing: 0;
        font-size: 12px;
        font-weight: 700;
        width: 100%;
        &:not(.v-tab--active) {
          color: $bh-black;
        }
      }
      .v-tab.v-tab--active {
        color: $bh-white;
      }
      .v-tab:hover::before {
        display: none !important;
      }
      background: $bh-blue--4 !important;
    }
  }
}

.journey-preview-cards {
  margin-top: 24px;
  padding: 0 8px;

  &--row {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 16px;
    align-items: start;
    max-height: 400px;
    overflow-y: auto;
    overflow-x: hidden;
    padding-right: 8px;

    // Custom scrollbar styling
    &::-webkit-scrollbar {
      width: 6px;
    }

    &::-webkit-scrollbar-track {
      background: $bh-gray--6;
      border-radius: 3px;
    }

    &::-webkit-scrollbar-thumb {
      background: $bh-blue;
      border-radius: 3px;

      &:hover {
        background: $bh-blue--2;
      }
    }

    // Firefox scrollbar
    scrollbar-width: thin;
    scrollbar-color: $bh-blue $bh-gray--6;
  }

  &--column {
    display: flex;
    flex-direction: column;
    gap: 16px;
    max-height: 400px;
    overflow-y: auto;
    overflow-x: hidden;
    padding-right: 8px;

    // Custom scrollbar styling
    &::-webkit-scrollbar {
      width: 6px;
    }

    &::-webkit-scrollbar-track {
      background: $bh-gray--6;
      border-radius: 3px;
    }

    &::-webkit-scrollbar-thumb {
      background: $bh-blue;
      border-radius: 3px;

      &:hover {
        background: $bh-blue--2;
      }
    }

    // Firefox scrollbar
    scrollbar-width: thin;
    scrollbar-color: $bh-blue $bh-gray--6;
  }
}
.regenerate-btn {
  margin-top: 24px;
  &-container {
    display: flex;
    justify-content: flex-end;
  }
  .v-btn__content {
    display: flex;
    align-items: center;
    gap: 8px;
  }
  span:not(.v-btn__content) {
    font-size: 12px;
    font-weight: 700;
    color: $bh-black;
    opacity: 0.5;
  }
  .v-icon {
    color: $bh-black !important;
    opacity: 0.5;
  }
}

.journey-generating-loader {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 400px;
  padding: 40px 20px;
  text-align: center;

  .loader-content {
    max-width: 400px;
  }

  .loader-title {
    color: $bh-black;
    font-size: 1.5rem;
    font-weight: 600;
    margin: 24px 0 16px 0;
  }

  .loader-description {
    color: $bh-black;
    font-size: 0.9rem;
    opacity: 0.7;
    line-height: 1.5;
    margin-bottom: 32px;
  }

  .loader-steps {
    display: flex;
    flex-direction: column;
    gap: 16px;
    align-items: flex-start;

    .step {
      display: flex;
      align-items: center;
      gap: 12px;
      color: $bh-black;
      opacity: 0.5;
      font-size: 0.9rem;
      font-weight: 500;

      &.active {
        opacity: 1;
        color: $bh-blue;

        .v-icon {
          color: $bh-blue !important;
        }
      }

      .v-icon {
        color: $bh-black !important;
      }

      span {
        flex: 1;
      }
    }
  }
}

.journey-generation-error {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 400px;
  padding: 40px 20px;
  text-align: center;

  .error-content {
    max-width: 400px;
  }

  .error-title {
    color: $bh-black;
    font-size: 1.5rem;
    font-weight: 600;
    margin: 24px 0 16px 0;
  }

  .error-description {
    color: $bh-black;
    font-size: 0.9rem;
    opacity: 0.7;
    line-height: 1.5;
    margin-bottom: 16px;
  }

  .error-suggestion {
    color: $bh-black;
    font-size: 0.9rem;
    font-weight: 500;
    margin-bottom: 32px;
  }

  .error-actions {
    display: flex;
    flex-direction: column;
    gap: 12px;
    width: 100%;
    max-width: 280px;

    .retry-btn {
      padding: 16px 24px !important;
      font-weight: 600;
    }

    .cancel-retry-btn {
      padding: 16px 24px !important;
      font-weight: 500;
    }
  }
}
</style>
